import * as React from 'react';
import { CheckOutlined, CloseOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import apptracker from '@src/utils/sensor/apptracker';
import * as Events from '@src/utils/sensor/track';
import { DISABLED_TOOLTIP_MESSAGE } from '../../constant';
import * as Store from '../store';
import AppendedAtom from './AppendedAtom';
import Rules from './Rules';
import AdvancedRules from './AdvancedRules';
import * as Styled from './styled';
import type * as Types from '../../../types';

function combineAdvancedFilterValue(
  filterValue: Types.FilterValue,
  advancedFilterValue?: Types.FilterRule[][],
) {
  if (!advancedFilterValue?.length) return filterValue;

  return { rule: [...filterValue.rule, ...advancedFilterValue] };
}

const FilterDialogSimpleFilter = () => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [showValidation, setShowValidation] = React.useState(false);
  const [filterValue] = Store.useFilterValue();
  const [filterStatus, setFilterStatus] = Store.useFilterStatus();
  const advancedFilterValue = Store.useAdvancedFilterValue();
  const filterValueValidated = Store.useFilterValueValidated();
  const compareMode = Store.useFilterCompareMode();
  const comareSeries = Store.useFilterCompareSeries();
  const disabled = Store.useFilterDisabled();
  const advancedFilterEnabled = Store.useAdvancedFilterEnabled();
  const resetFilterValue = Store.useResetFilterValue();
  const onCompareSeriesChange = Store.useOnCompareSeriesChange();
  const onChange = Store.useOnChange();
  const onExitAdvancedFilter = Store.useOnExitAdvancedFilter();
  const onResetAdvancedFilter = Store.useOnResetAdvancedFilter();

  const filterValueRef = React.useRef(filterValue);
  const filterValueValidatedRef = React.useRef(filterValueValidated);
  filterValueRef.current = filterValue;
  filterValueValidatedRef.current = filterValueValidated;

  React.useEffect(() => {
    setShowValidation(!filterValueValidated);
  }, [filterValueValidated]);

  const handleApply = React.useCallback(() => {
    // Delay to the next frame to avoid missing the state update from onBlur
    requestAnimationFrame(() => {
      if (!filterValueValidatedRef.current) {
        setShowValidation(true);
        return;
      }

      const changes = combineAdvancedFilterValue(filterValueRef.current, advancedFilterValue);

      onChange?.(changes);
      setFilterStatus('idle');

      apptracker(Events.FILTER_BUILDER_APPLY_CHANGES_ON_SIMPLE_MODE, {
        filter: JSON.stringify(filterValueRef.current.rule),
      });
    });
  }, [onChange, setFilterStatus, advancedFilterValue]);

  const handleCancel = React.useCallback(() => {
    if (filterStatus === 'editing') {
      setFilterStatus('idle');
      resetFilterValue();
      onResetAdvancedFilter?.();
      return;
    }

    onChange?.({ rule: [] });
    apptracker(Events.CLEAR_FILTER_ON_FILTER_BUILDER, {});
  }, [filterStatus, setFilterStatus, resetFilterValue, onChange, onResetAdvancedFilter]);

  const shouldShowCheckButton = filterStatus === 'editing';
  const shouldShowClearButton =
    filterStatus === 'editing' ||
    ((!!filterValue.rule.length || !!filterValue.fid) &&
      !['editing-advanced', 'simple-saving'].includes(filterStatus));

  // Don't show the Appended Atom if component is disabled, and show a tooltip message
  const showAppendAtom = !disabled || !filterValue.rule.length;
  const showDisabledTooltip =
    !compareMode && disabled && !filterValue.rule.length && !advancedFilterEnabled;

  return (
    <Tooltip title={showDisabledTooltip ? DISABLED_TOOLTIP_MESSAGE : undefined} placement="right">
      <Styled.Container
        ref={containerRef}
        $compareMode={compareMode}
        $comareSeries={comareSeries}
        style={showDisabledTooltip ? { cursor: 'not-allowed' } : undefined}
      >
        <Rules showValidation={showValidation} />

        <AdvancedRules />

        <Styled.ActionContainer>
          {showAppendAtom && <AppendedAtom />}

          {compareMode && (
            <Button
              type="text"
              icon={comareSeries ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              onClick={() => onCompareSeriesChange?.(!comareSeries)}
            />
          )}

          {shouldShowCheckButton && (
            <Button
              type="primary"
              ghost
              icon={<CheckOutlined />}
              // Use onMouseDown to ensure the apply action is triggered before the blur event
              onMouseDown={handleApply}
            />
          )}
          {shouldShowClearButton && (
            <Button
              type={filterStatus === 'editing' ? 'default' : 'text'}
              icon={<CloseOutlined />}
              onClick={handleCancel}
            />
          )}

          {advancedFilterEnabled && (
            <Button
              onMouseDown={onExitAdvancedFilter}
              style={{
                padding: '0 6px',
                lineHeight: '12px',
                height: 26,
                fontSize: 12,
                background: '#FFFEE8',
                borderColor: '#A5A69F',
              }}
            >
              Exit Advanced Filter
            </Button>
          )}
        </Styled.ActionContainer>
      </Styled.Container>
    </Tooltip>
  );
};

export default React.memo(FilterDialogSimpleFilter);
